import { useLogin } from '@/frontend/hooks/useAuth'
import logoUrl from '../../logo.svg'
import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import { TbAlertCircle, TbLock, TbLogin, TbMail } from 'react-icons/tb'

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: (search.error as string) || undefined,
  }),
  beforeLoad: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData({
        queryKey: ['auth', 'session'],
        queryFn: () => fetch('/api/auth/session', { credentials: 'include' }).then((r) => r.json()),
      })
      if (data?.user) {
        const dest = data.user.role === 'DEVELOPER' ? '/dev' : data.user.role === 'USER' ? '/profile' : '/dashboard'
        throw redirect({ to: dest })
      }
    } catch (e) {
      if (e instanceof Error) return
      throw e
    }
  },
  component: LoginPage,
})

const OAUTH_ERRORS: Record<string, string> = {
  google_denied: 'Login dengan Google dibatalkan.',
  invalid_state: 'Sesi OAuth tidak valid, silakan coba lagi.',
  token_failed: 'Gagal menukar token Google, silakan coba lagi.',
  userinfo_failed: 'Gagal mengambil info akun Google, silakan coba lagi.',
  account_disabled: 'Akun Anda telah dinonaktifkan. Hubungi admin untuk informasi lebih lanjut.',
}

function LoginPage() {
  const login = useLogin()
  const { error: searchError } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    login.mutate({ email, password })
  }

  const errorMessage = login.isError
    ? login.error.message
    : searchError
      ? (OAUTH_ERRORS[searchError] ?? 'Login dengan Google gagal, silakan coba lagi.')
      : null

  return (
    <Box style={{ minHeight: '100vh', background: '#1a1a2e', position: 'relative', overflow: 'hidden' }}>
      {/* background blobs */}
      <Box style={{
        position: 'absolute', top: '-15%', left: '-10%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box style={{
        position: 'absolute', bottom: '-20%', right: '-10%',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(79,70,229,0.2) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box style={{
        position: 'absolute', top: '50%', left: '60%',
        width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <Center mih="100vh" style={{ position: 'relative', zIndex: 1 }}>
        <Box
          p="xl"
          w={400}
          style={{
            background: 'rgba(36,36,36,0.75)',
            backdropFilter: 'blur(20px)',
            borderRadius: 20,
            border: '1px solid rgba(124,58,237,0.35)',
            boxShadow: '0 0 0 1px rgba(124,58,237,0.1), 0 8px 32px rgba(0,0,0,0.4), 0 0 60px rgba(124,58,237,0.12)',
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {/* header */}
              <Stack gap={8} align="center" mb={4}>
                <img
                  src={logoUrl}
                  width={56}
                  height={56}
                  alt="logo"
                  style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(124,58,237,0.45)', display: 'block' }}
                />
                <Title order={2} fw={700} ta="center" c="white">
                  Monitoring System
                </Title>
                <Text c="dimmed" size="sm" ta="center">
                  Masuk untuk melanjutkan
                </Text>
              </Stack>

              {errorMessage && (
                <Alert icon={<TbAlertCircle size={16} />} color="red" variant="light">
                  {errorMessage}
                </Alert>
              )}

              <TextInput
                label="Email"
                placeholder="email@example.com"
                leftSection={<TbMail size={16} />}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
              />

              <PasswordInput
                label="Password"
                placeholder="Password"
                leftSection={<TbLock size={16} />}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
              />

              <Button
                type="submit"
                fullWidth
                leftSection={<TbLogin size={18} />}
                loading={login.isPending}
                mt={4}
                style={{
                  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  border: 'none',
                }}
              >
                Sign in
              </Button>

              <Divider label="atau" labelPosition="center" />

              <Button
                variant="default"
                fullWidth
                leftSection={<FcGoogle size={18} />}
                onClick={() => { window.location.href = '/api/auth/google' }}
              >
                Continue with Google
              </Button>
            </Stack>
          </form>
        </Box>
      </Center>
    </Box>
  )
}
